import { resetPassword, sendResetPasswordEmail, validateToken } from 'api/auth/platform-identity';
import RoutesList from 'config/constants';
import AuthLayout from 'layouts/auth/AuthLayout';
import AccountCheckEmailSendView from 'pages/auth/account-activate/AccountCheckEmailSendView';
import AccountLinkExpiredView from 'pages/auth/account-activate/AccountLinkExpiredView';
import AccountLinkInvalidView from 'pages/auth/account-activate/AccountLinkInvalidView';
import AccountSuccessView from 'pages/auth/account-activate/AccountSuccessView';
import ActivationAccountView from 'pages/auth/account-activate/ActivationAccountView';
import ErrorPage from 'pages/common/error';
import qs from 'query-string';
import { Fragment, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from 'redux/platform/hooks';
import { getPlatformBaseUrl } from 'utils/getBaseUrl';
import getErrorMessage from 'utils/getErrorMessage';

import { Spinner } from '@limepayments/cosmic';

import AuthRoutesList from '../constants';

type FormikFormValues = {
  newPassword: string;
  confirmNewPassword: string;
  termsAgreed: boolean;
};

function ActivateAccountPage() {
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [tokenErrorMessage, setTokenErrorMessage] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showConfirmSection, setShowConfirmSection] = useState<boolean>(false);
  const [isPageLoading, setIsPageLoading] = useState<boolean>(true);
  const [expiredToken, setExpiredToken] = useState<boolean>(false);
  const [invalidToken, setInvalidToken] = useState<boolean>(false);
  const [isValidateTokenLoading, setIsValidateTokenLoading] = useState<boolean>(false);
  const [showAccountEmailSentSection, setShowAccountEmailSentSection] = useState<boolean>(false);
  const [resentActivationLoader, setResentActivationLoader] = useState<boolean>(false);
  const [initialValues, setInitialValues] = useState<FormikFormValues>({
    newPassword: '',
    confirmNewPassword: '',
    termsAgreed: false,
  });

  const { apiBaseUri, marketplaceId, platformName } = useAppSelector((state) => ({
    apiBaseUri: state.config.apiBaseUri,
    marketplaceId: state.config.marketplaceId,
    platformName: state.config.platformName,
  }));

  const { ref } = qs.parse(window.location.search);

  useEffect(() => {
    if (marketplaceId) {
      setIsPageLoading(false);
    }
  }, [setIsPageLoading, marketplaceId]);

  useEffect(() => {
    if (!ref || !apiBaseUri || !platformName) {
      setTokenErrorMessage('Not all required parameters are set');
      return;
    }

    setTokenErrorMessage('');
    setIsValidateTokenLoading(true);
    validateToken(apiBaseUri, ref as string)
      .then(({ isExpired, emailAddress }) => {
        setErrorMessage('');
        setEmail(emailAddress);

        if (isExpired) {
          setExpiredToken(true);
        }
      })
      .catch((e) => {
        setInvalidToken(true);
      })
      .finally(() => {
        setIsValidateTokenLoading(false);
      });
  }, [apiBaseUri, platformName, ref]);

  const submitHandler = async (values: FormikFormValues, formObj: { resetForm: () => void }) => {
    try {
      setErrorMessage('');
      setIsLoading(true);

      await resetPassword(apiBaseUri, ref as string, { password: values.newPassword });

      setInitialValues(initialValues);
      formObj.resetForm();

      setShowConfirmSection(true);
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const resendActivateLinkHandler = async () => {
    try {
      setErrorMessage('');
      setResentActivationLoader(true);

      await sendResetPasswordEmail(apiBaseUri, {
        marketplaceId: marketplaceId,
        emailAddress: email,
      });

      setShowAccountEmailSentSection(true);
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    } finally {
      setResentActivationLoader(false);
    }
  };

  return (
    <Fragment>
      {!isPageLoading && !isValidateTokenLoading && (
        <Fragment>{tokenErrorMessage.length > 0 && <ErrorPage bodyText={tokenErrorMessage} />}</Fragment>
      )}

      {(isPageLoading || isValidateTokenLoading) && (
        <div className="spinner-wrapper">
          <Spinner variant="simple" isVisible />
        </div>
      )}

      {(isPageLoading || isValidateTokenLoading || !tokenErrorMessage.length) && (
        <AuthLayout title="Activate account">
          <Fragment>
            {invalidToken && <AccountLinkInvalidView onSubmitClick={() => resendActivateLinkHandler()} />}

            {expiredToken && !showAccountEmailSentSection && (
              <AccountLinkExpiredView
                onSubmitClick={() => resendActivateLinkHandler()}
                email={email}
                resentActivationLoader={resentActivationLoader}
              />
            )}

            {!isPageLoading && !isValidateTokenLoading && !tokenErrorMessage.length && !invalidToken && !expiredToken && (
              <Fragment>
                {!showConfirmSection && !showAccountEmailSentSection && (
                  <ActivationAccountView
                    onSubmitClick={submitHandler}
                    initialValues={initialValues}
                    passwordUpdateRequestLoader={isLoading}
                    errorMsg={errorMessage}
                    email={email}
                  />
                )}

                {showConfirmSection && !showAccountEmailSentSection && (
                  <AccountSuccessView
                    onSubmitClick={() =>
                      navigate(
                        `${getPlatformBaseUrl(platformName)}/${RoutesList.AUTH_ROUTE}/${AuthRoutesList.LOGIN_PAGE}`,
                      )
                    }
                  />
                )}
              </Fragment>
            )}

            {!isPageLoading &&
              !isValidateTokenLoading &&
              !tokenErrorMessage.length &&
              !invalidToken &&
              showAccountEmailSentSection &&
              !showConfirmSection && <AccountCheckEmailSendView email={email} />}
          </Fragment>
        </AuthLayout>
      )}
    </Fragment>
  );
}

export default ActivateAccountPage;
